import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import KPICards from "./KPICards";
import CampaignForm from "./CampaignForm";
import CampaignsTable from "./CampaignsTable";
import ChartsSection from "./ChartsSection";
import LandingModal from "./LandingModal";
import VisitLogModal from "./VisitLogModal";
import { generateDemoData } from "./demoData";
import { randomIp, randomAvatar, randomFullName, randomPhone } from "./utils";

const LoyaltyQRDashboard = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [visits, setVisits] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [landingModalOpen, setLandingModalOpen] = useState(false);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logCampaignId, setLogCampaignId] = useState(null);

  // Инициализация демо-данных
  useEffect(() => {
    const { campaigns: demoCampaigns, visits: demoVisits } = generateDemoData();
    setCampaigns(demoCampaigns);
    setVisits(demoVisits);
  }, []);

  // KPI расчёт
  const kpi = useMemo(() => {
    const totalTransitions = visits.length;
    const totalRegistrations = visits.filter(v => v.targetReached).length;
    const totalClicks = visits.filter(v => v.clickTarget).length;
    const activePages = campaigns.filter(c => c.status === 'active').length;
    const conversionRate = totalTransitions === 0 ? 0 : Math.round((totalRegistrations / totalTransitions) * 100);
    return { totalTransitions, totalRegistrations, totalClicks, activePages, conversionRate };
  }, [visits, campaigns]);

  // Создание новой кампании
  const handleCreateCampaign = useCallback((newCampaign) => {
    const id = Date.now();
    const shortCode = 'qrrun.ru/' + id.toString(36) + Math.floor(Math.random() * 1000).toString(36);
    const campaign = {
      ...newCampaign,
      id,
      shortUrl: shortCode,
      createdAt: new Date().toLocaleDateString('ru-RU'),
      status: 'active',
    };
    setCampaigns(prev => [...prev, campaign]);
  }, []);

  // Показать лендинг (симулирует переход)
  const handleShowLanding = useCallback((campaign) => {
    setSelectedCampaign(campaign);
    // Имитация перехода: добавляем визит без цели
    const newVisit = {
      id: Date.now() + '-' + Math.random().toString(36),
      pageId: campaign.id,
      ip: randomIp(),
      avatar: randomAvatar(),
      fullName: randomFullName(),
      phone: randomPhone(),
      sourceType: null,
      date: new Date().toLocaleDateString('ru-RU'),
      time: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
      targetReached: false,
      clickTarget: false,
    };
    setVisits(prev => [...prev, newVisit]);
    setLandingModalOpen(true);
  }, []);

  // Завершение целевого действия (регистрация через кнопку)
  const handleCompleteRegistration = useCallback((source) => {
    if (!selectedCampaign) return;
    // Обновляем последний визит для этой страницы
    setVisits(prev => {
      const lastVisitIndex = prev.findLastIndex(v => v.pageId === selectedCampaign.id);
      if (lastVisitIndex !== -1) {
        const updated = [...prev];
        updated[lastVisitIndex] = {
          ...updated[lastVisitIndex],
          targetReached: true,
          clickTarget: true,
          sourceType: source,
        };
        return updated;
      }
      return prev;
    });
    setLandingModalOpen(false);
  }, [selectedCampaign]);

  const handleShowLog = useCallback((campaignId) => {
    setLogCampaignId(campaignId);
    setLogModalOpen(true);
  }, []);

  return (
    <div className="max-w-[1600px] mx-auto px-4 py-6 md:py-8 space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-slate-800 to-blue-700 bg-clip-text text-transparent">
            Loyalty<span className="text-blue-600">QR</span> Pro
          </h1>
          <p className="text-slate-500 mt-1 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
            Генератор карт лояльности + UTM-трекинг · аналитика · мини-лендинги
          </p>
        </div>
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl px-4 py-2 shadow-sm border text-sm">
          <span className="font-mono font-medium text-blue-700">qr_cashier</span> | теги: loyalty, offline
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Левая колонка: форма */}
        <div className="xl:col-span-1">
          <CampaignForm onCreate={handleCreateCampaign} />
          <Card className="mt-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-100">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600">
                Автоматический UTM-трекинг: <strong>utm_source=qr_cashier, utm_medium=qr_physical, utm_campaign=loyalty_card</strong>.
                Клики по кнопкам (Max / Telegram / СМС) фиксируются как «Регистрация».
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Правая колонка: KPI, графики, таблица */}
        <div className="xl:col-span-2 space-y-6">
          <KPICards {...kpi} />

          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList>
              <TabsTrigger value="overview">Обзор</TabsTrigger>
              <TabsTrigger value="campaigns">Кампании</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="space-y-4">
              <ChartsSection visits={visits} />
            </TabsContent>
            <TabsContent value="campaigns">
              <CampaignsTable
                campaigns={campaigns}
                visits={visits}
                onShowLanding={handleShowLanding}
                onShowLog={handleShowLog}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <LandingModal
        open={landingModalOpen}
        onOpenChange={setLandingModalOpen}
        campaign={selectedCampaign}
        onComplete={handleCompleteRegistration}
      />

      <VisitLogModal
        open={logModalOpen}
        onOpenChange={setLogModalOpen}
        campaignId={logCampaignId}
        campaigns={campaigns}
        visits={visits}
      />
    </div>
  );
};

export default LoyaltyQRDashboard;